// Browser audio for WhatsApp calls. Meta's media server takes one complete SDP (no trickle ICE),
// so every offer and answer waits for candidate gathering before it is sent.
const ICE_SERVERS: RTCIceServer[] = [{ urls: "stun:stun.l.google.com:19302" }];
const ICE_GATHER_TIMEOUT_MS = 3_000;

export type CallMedia = {
  peer: RTCPeerConnection;
  microphone: MediaStream;
  audio: HTMLAudioElement;
  // The customer's answer is applied once, however many places learn about it
  answerApplied?: Promise<void>;
};

function waitForIceGathering(peer: RTCPeerConnection) {
  if (peer.iceGatheringState === "complete") return Promise.resolve();
  return new Promise<void>((resolve) => {
    // Host and STUN candidates arrive quickly; a stalled TURN-less gather shouldn't hold the call
    const timer = window.setTimeout(() => done(), ICE_GATHER_TIMEOUT_MS);
    const done = () => {
      window.clearTimeout(timer);
      peer.removeEventListener("icegatheringstatechange", onChange);
      resolve();
    };
    const onChange = () => {
      if (peer.iceGatheringState === "complete") done();
    };
    peer.addEventListener("icegatheringstatechange", onChange);
  });
}

export type CallMediaEvents = {
  // The audio path can't recover; the call should end
  onFailed: () => void;
  // Brief network trouble (true) and its recovery (false)
  onReconnecting: (reconnecting: boolean) => void;
};

/** A friendly reason for a microphone or audio setup error. */
export function callMediaErrorMessage(error: unknown, action: "answer" | "place") {
  const name = error instanceof Error ? error.name : "";
  if (name === "NotAllowedError" || name === "SecurityError") {
    return `Allow microphone access for this site to ${action === "answer" ? "take" : "place"} calls, then try again`;
  }
  if (name === "NotFoundError" || name === "OverconstrainedError") return "No microphone found. Connect one and try again";
  if (name === "NotReadableError" || name === "AbortError") return "Your microphone is in use by another app. Close it and try again";
  return error instanceof Error && error.message ? error.message : `Could not ${action} the call`;
}

async function openMedia(events: CallMediaEvents): Promise<CallMedia> {
  const microphone = await navigator.mediaDevices.getUserMedia({ audio: true });
  try {
    const peer = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    const audio = new Audio();
    audio.autoplay = true;
    peer.addEventListener("track", (event) => {
      audio.srcObject = event.streams[0] ?? new MediaStream([event.track]);
      void audio.play().catch(() => undefined);
    });
    // No TURN relay: a network that blocks direct media fails here rather than going silent
    peer.addEventListener("connectionstatechange", () => {
      if (peer.connectionState === "failed") events.onFailed();
      else if (peer.connectionState === "disconnected") events.onReconnecting(true);
      else if (peer.connectionState === "connected") events.onReconnecting(false);
    });
    for (const track of microphone.getTracks()) peer.addTrack(track, microphone);
    return { peer, microphone, audio };
  } catch (error) {
    for (const track of microphone.getTracks()) track.stop();
    throw error;
  }
}

export async function answerCallOffer(offerSdp: string, events: CallMediaEvents): Promise<{ media: CallMedia; sdp: string }> {
  const media = await openMedia(events);
  try {
    await media.peer.setRemoteDescription({ type: "offer", sdp: offerSdp });
    await media.peer.setLocalDescription(await media.peer.createAnswer());
    await waitForIceGathering(media.peer);
    return { media, sdp: media.peer.localDescription!.sdp };
  } catch (error) {
    closeCallMedia(media);
    throw error;
  }
}

export async function createCallOffer(events: CallMediaEvents): Promise<{ media: CallMedia; sdp: string }> {
  const media = await openMedia(events);
  try {
    await media.peer.setLocalDescription(await media.peer.createOffer({ offerToReceiveAudio: true }));
    await waitForIceGathering(media.peer);
    return { media, sdp: media.peer.localDescription!.sdp };
  } catch (error) {
    closeCallMedia(media);
    throw error;
  }
}

export function applyCallAnswer(media: CallMedia, answerSdp: string) {
  media.answerApplied ??= media.peer.setRemoteDescription({ type: "answer", sdp: answerSdp });
  return media.answerApplied;
}

export function setCallMuted(media: CallMedia, muted: boolean) {
  for (const track of media.microphone.getAudioTracks()) track.enabled = !muted;
}

export function closeCallMedia(media: CallMedia | null | undefined) {
  if (!media) return;
  for (const track of media.microphone.getTracks()) track.stop();
  media.peer.close();
  media.audio.srcObject = null;
}
